import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PageWrapper, Card, Button } from "../components";
import { 
  ArrowLeft, Mic, Upload, Trash2, Globe, Lock, 
  Check, Volume2, AlertCircle, Sparkles, Play, Pause, Loader2,
  Gauge, Star
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Voice speed options with icons
const SPEED_OPTIONS = [
  { key: "slow", icon: "🌙", color: "from-indigo-400 to-purple-500" },
  { key: "default", icon: "✨", color: "from-emerald-400 to-teal-500" },
  { key: "normal", icon: "⚡", color: "from-amber-400 to-orange-500" }
];

export const VoiceSettingsPage = () => {
  const navigate = useNavigate();
  const audioRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [voices, setVoices] = useState({ default_voices: {}, custom_voices: {} });
  const [myVoices, setMyVoices] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Voice speed state
  const [voiceSpeed, setVoiceSpeed] = useState("default");
  const [speedOptions, setSpeedOptions] = useState({});
  const [speedLoading, setSpeedLoading] = useState(false);
  
  // Default voice preference state
  const [defaultVoice, setDefaultVoice] = useState(null);
  const [defaultVoiceLoading, setDefaultVoiceLoading] = useState(false);
  
  // Preview state
  const [previewingVoice, setPreviewingVoice] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Delete loading state
  const [deletingVoice, setDeletingVoice] = useState(null);

  const getToken = () => localStorage.getItem("reclaim_token");

  useEffect(() => {
    fetchVoices();
    fetchVoiceSpeed();
    fetchDefaultVoice();
  }, []);

  const fetchDefaultVoice = async () => {
    const authToken = getToken();
    if (!authToken) return;
    
    try {
      const res = await fetch(`${API_URL}/api/settings/default-voice`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.voice_id) {
          setDefaultVoice({ id: data.voice_id, name: data.voice_name });
        }
      }
    } catch (e) {
      console.error("Error fetching default voice:", e);
    }
  };

  const handleSetDefaultVoice = async (voiceId, voiceName) => {
    const authToken = getToken();
    if (!authToken) return;
    
    setDefaultVoiceLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/settings/default-voice?voice_id=${encodeURIComponent(voiceId)}&voice_name=${encodeURIComponent(voiceName)}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (res.ok) {
        setDefaultVoice({ id: voiceId, name: voiceName });
        setSuccess(`"${voiceName}" set as your default voice`);
      } else {
        throw new Error("Failed to set default voice");
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setDefaultVoiceLoading(false);
    }
  };

  const fetchVoiceSpeed = async () => {
    const authToken = getToken();
    if (!authToken) return;
    
    try {
      const res = await fetch(`${API_URL}/api/settings/voice-speed`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setVoiceSpeed(data.current);
        setSpeedOptions(data.options);
      }
    } catch (e) {
      console.error("Error fetching voice speed:", e);
    }
  };

  const handleSpeedChange = async (speedKey) => {
    const authToken = getToken();
    if (!authToken) return;
    
    setSpeedLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/settings/voice-speed/${speedKey}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (res.ok) {
        setVoiceSpeed(speedKey);
        setSuccess("Voice speed preference saved");
      } else {
        throw new Error("Failed to update voice speed");
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSpeedLoading(false);
    }
  };

  const fetchVoices = async () => {
    const authToken = getToken();
    if (!authToken) {
      setLoading(false);
      return;
    }
    
    try {
      const [availableRes, myRes] = await Promise.all([
        fetch(`${API_URL}/api/voices/available`, {
          headers: { Authorization: `Bearer ${authToken}` }
        }),
        fetch(`${API_URL}/api/voices/my-voices`, {
          headers: { Authorization: `Bearer ${authToken}` }
        })
      ]);
      
      const availableData = await availableRes.json();
      const myData = await myRes.json();
      
      setVoices(availableData);
      setMyVoices(myData.voices || []);
    } catch (e) {
      console.error("Error fetching voices:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVoice = async (voiceId, voiceName) => {
    if (!window.confirm(`Are you sure you want to delete "${voiceName}"?`)) return;
    
    setDeletingVoice(voiceId);
    setError(null);
    
    const authToken = getToken();
    try {
      const res = await fetch(`${API_URL}/api/voices/${voiceId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to delete voice");
      }
      
      setSuccess("Voice deleted successfully");
      
      // Remove from local state immediately for better UX
      setMyVoices(prev => prev.filter(v => v.id !== voiceId));
      
      // Clear default voice if it was the deleted one
      if (defaultVoice?.id === voiceId) {
        setDefaultVoice(null);
      }
      
      // Refresh voices list
      fetchVoices();
    } catch (e) {
      console.error("Delete voice error:", e);
      setError(e.message);
    } finally {
      setDeletingVoice(null);
    }
  };

  const handleTogglePublic = async (voiceId, currentStatus) => {
    const authToken = getToken();
    try {
      const res = await fetch(`${API_URL}/api/voices/${voiceId}/visibility?is_public=${!currentStatus}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (!res.ok) throw new Error("Failed to update visibility");
      
      fetchVoices();
    } catch (e) {
      setError(e.message);
    }
  };

  const handlePreviewVoice = async (voiceId) => {
    // If already playing this voice, stop it
    if (previewingVoice === voiceId && isPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setIsPlaying(false);
      setPreviewingVoice(null);
      return;
    }

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
    }

    setPreviewLoading(true);
    setPreviewingVoice(voiceId);
    setError(null);

    const authToken = getToken();
    try {
      const res = await fetch(`${API_URL}/api/voices/${voiceId}/preview`, {
        method: "POST",
        headers: { Authorization: `Bearer ${authToken}` }
      });

      if (!res.ok) {
        const data = await res.json();
        // Handle both string and object error details
        const errorMsg = typeof data.detail === 'string' 
          ? data.detail 
          : (data.detail?.message || "Failed to generate preview");
        throw new Error(errorMsg);
      }

      const data = await res.json();
      
      // Create and play audio
      if (audioRef.current) {
        audioRef.current.src = data.audio_url;
        await audioRef.current.play();
        setIsPlaying(true);
      }
    } catch (e) {
      setError(e.message);
      setPreviewingVoice(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setPreviewingVoice(null);
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (loading) {
    return (
      <PageWrapper>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#4A6741] border-t-transparent rounded-full animate-spin" />
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <div className="min-h-screen bg-[#FAFAF7] pb-24">
        {/* Hidden audio element for previews */}
        <audio ref={audioRef} onEnded={handleAudioEnded} />

        {/* Header */}
        <div className="bg-gradient-to-b from-[#2D4A2A] to-[#3D5A34] text-white px-6 pt-6 pb-12">
          <div className="max-w-lg mx-auto">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Back</span>
            </button>
            
            <h1 className="font-heading text-3xl mb-2">Voice Settings</h1>
            <p className="text-white/70">
              Create personalized meditation audio with your own voice
            </p>
          </div>
        </div>

        <div className="px-6 -mt-6 max-w-lg mx-auto space-y-6">
          {/* Notifications */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-3"
              >
                <AlertCircle size={20} />
                <span>{error}</span>
              </motion.div>
            )}
            {success && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl flex items-center gap-3"
              >
                <Check size={20} />
                <span>{success}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Create Voice Card */}
          <Card className="p-6 bg-gradient-to-br from-[#F8F7F5] to-white border-2 border-dashed border-[#D4A373]/30">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-[#D4A373]/10 rounded-full flex items-center justify-center mx-auto">
                <Mic size={28} className="text-[#D4A373]" />
              </div>
              <div>
                <h3 className="font-heading text-xl text-[#1F2937] mb-2">
                  Clone Your Voice
                </h3>
                <p className="text-[#6B7280] text-sm">
                  Upload voice samples to create personalized meditation audio that sounds like you
                </p>
              </div>
              <Button
                onClick={() => navigate("/voice-upload")}
                className="w-full"
                data-testid="create-voice-btn"
              >
                <Upload size={18} />
                Upload Voice Samples
              </Button>
            </div>
          </Card>

          {/* Voice Speed Preference */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-400/20 to-purple-500/20 rounded-full flex items-center justify-center">
                <Gauge size={20} className="text-violet-500" />
              </div>
              <div>
                <h3 className="font-heading text-lg text-[#1F2937]">Voice Speed</h3>
                <p className="text-xs text-[#6B7280]">Adjust pacing for performance rituals</p>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-3">
              {SPEED_OPTIONS.map((option) => {
                const details = speedOptions[option.key];
                const isSelected = voiceSpeed === option.key;
                
                return (
                  <button
                    key={option.key}
                    onClick={() => handleSpeedChange(option.key)}
                    disabled={speedLoading}
                    className={`relative p-4 rounded-xl border-2 transition-all ${
                      isSelected 
                        ? 'border-[#4A6741] bg-[#4A6741]/5' 
                        : 'border-[#E6E2DD] hover:border-[#4A6741]/30 bg-white'
                    }`}
                    data-testid={`voice-speed-${option.key}`}
                  >
                    {isSelected && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-[#4A6741] rounded-full flex items-center justify-center"
                      >
                        <Check size={12} className="text-white" />
                      </motion.div>
                    )}
                    <div className="text-center space-y-2">
                      <span className="text-2xl">{option.icon}</span>
                      <p className="font-medium text-sm text-[#1F2937]">
                        {details?.label || option.key}
                      </p>
                      <p className="text-xs text-[#6B7280] line-clamp-2">
                        {details?.description || ""}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
            
            {speedLoading && (
              <div className="mt-3 flex items-center justify-center gap-2 text-[#6B7280] text-sm">
                <Loader2 size={14} className="animate-spin" />
                <span>Saving...</span>
              </div>
            )}
          </Card>

          {/* My Voices */}
          {myVoices.length > 0 && (
            <section className="space-y-4">
              <h2 className="font-heading text-xl text-[#1F2937]">My Voices</h2>
              <div className="space-y-3">
                {myVoices.map((voice) => (
                  <Card key={voice.id} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {/* Play/Pause Button */}
                        <button
                          onClick={() => handlePreviewVoice(voice.id)}
                          disabled={previewLoading && previewingVoice === voice.id}
                          className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                            previewingVoice === voice.id && isPlaying
                              ? 'bg-[#4A6741] text-white'
                              : 'bg-[#4A6741]/10 text-[#4A6741] hover:bg-[#4A6741]/20'
                          }`}
                          data-testid={`preview-voice-${voice.id}`}
                        >
                          {previewLoading && previewingVoice === voice.id ? (
                            <Loader2 size={20} className="animate-spin" />
                          ) : previewingVoice === voice.id && isPlaying ? (
                            <Pause size={20} />
                          ) : (
                            <Play size={20} className="ml-0.5" />
                          )}
                        </button>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-[#1F2937]">{voice.voice_name}</h3>
                            {defaultVoice?.id === voice.id && (
                              <span className="text-xs bg-[#4A6741]/10 text-[#4A6741] px-2 py-0.5 rounded-full flex items-center gap-1">
                                <Star size={10} className="fill-current" />
                                Default
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-[#6B7280]">
                            Created {new Date(voice.created_at).toLocaleDateString()}
                          </p>
                          {previewingVoice === voice.id && isPlaying && (
                            <p className="text-xs text-[#4A6741] mt-1">Playing preview...</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Set as Default Button */}
                        {defaultVoice?.id !== voice.id && (
                          <button
                            onClick={() => handleSetDefaultVoice(voice.id, voice.voice_name)}
                            disabled={defaultVoiceLoading}
                            className="p-2 rounded-full bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors"
                            title="Set as default voice"
                            data-testid={`set-default-voice-${voice.id}`}
                          >
                            {defaultVoiceLoading ? (
                              <Loader2 size={18} className="animate-spin" />
                            ) : (
                              <Star size={18} />
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => handleTogglePublic(voice.id, voice.is_public)}
                          className={`p-2 rounded-full transition-colors ${
                            voice.is_public 
                              ? 'bg-[#4A6741]/10 text-[#4A6741]' 
                              : 'bg-[#F3F4F6] text-[#6B7280]'
                          }`}
                          title={voice.is_public ? "Public" : "Private"}
                        >
                          {voice.is_public ? <Globe size={18} /> : <Lock size={18} />}
                        </button>
                        <button
                          onClick={() => handleDeleteVoice(voice.id, voice.voice_name)}
                          disabled={deletingVoice === voice.id}
                          className="p-2 rounded-full bg-red-50 text-red-500 hover:bg-red-100 transition-colors disabled:opacity-50"
                          data-testid={`delete-voice-${voice.id}`}
                        >
                          {deletingVoice === voice.id ? (
                            <Loader2 size={18} className="animate-spin" />
                          ) : (
                            <Trash2 size={18} />
                          )}
                        </button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </section>
          )}

          {/* Default Voices */}
          {Object.keys(voices.default_voices || {}).length > 0 && (
            <section className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-xl text-[#1F2937]">Default Voices</h2>
                {defaultVoice && (
                  <span className="text-sm text-[#6B7280]">
                    Current: <span className="font-medium text-[#4A6741]">{defaultVoice.name}</span>
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(voices.default_voices).map(([key, voice]) => {
                  const isDefault = defaultVoice?.id === voice.id;
                  return (
                    <Card 
                      key={key} 
                      className={`p-4 cursor-pointer transition-all ${
                        isDefault 
                          ? 'ring-2 ring-[#4A6741] bg-[#4A6741]/5' 
                          : 'hover:bg-[#F9F9F7]'
                      }`}
                      onClick={() => handleSetDefaultVoice(voice.id, voice.name)}
                      data-testid={`default-voice-${key}`}
                    >
                      <div className="text-center space-y-2">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto ${
                          isDefault ? 'bg-[#4A6741] text-white' : 'bg-[#F3F4F6] text-[#6B7280]'
                        }`}>
                          {isDefault ? (
                            <Star size={18} className="fill-current" />
                          ) : (
                            <Sparkles size={18} />
                          )}
                        </div>
                        <h3 className="font-medium text-[#1F2937] text-sm">{voice.name}</h3>
                        <p className="text-xs text-[#6B7280] line-clamp-2">{voice.description}</p>
                        {isDefault && (
                          <span className="inline-flex items-center gap-1 text-xs text-[#4A6741] font-medium">
                            <Check size={12} />
                            Selected
                          </span>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </section>
          )}

          {/* Tips */}
          <Card className="p-5 bg-[#F0EFEB]/50">
            <h3 className="font-heading text-lg text-[#1F2937] mb-3">Tips for Best Results</h3>
            <ul className="space-y-2 text-sm text-[#4A4F4A]">
              <li className="flex items-start gap-2">
                <Check size={16} className="text-[#4A6741] mt-0.5 flex-shrink-0" />
                <span>Provide at least 1 minute of clear audio</span>
              </li>
              <li className="flex items-start gap-2">
                <Check size={16} className="text-[#4A6741] mt-0.5 flex-shrink-0" />
                <span>Record in a quiet environment with minimal background noise</span>
              </li>
              <li className="flex items-start gap-2">
                <Check size={16} className="text-[#4A6741] mt-0.5 flex-shrink-0" />
                <span>Speak naturally at a calm, meditation-friendly pace</span>
              </li>
              <li className="flex items-start gap-2">
                <Check size={16} className="text-[#4A6741] mt-0.5 flex-shrink-0" />
                <span>Include varied sentences and tones for best results</span>
              </li>
            </ul>
          </Card>
        </div>
      </div>
    </PageWrapper>
  );
};

export default VoiceSettingsPage;
